import { addDays } from 'date-fns';
import { ImageField } from '@prismicio/client';
import { IStoreMetadata } from 'data/Utils';
import {
  AgeGateDocumentData,
  AgeGateSlice,
  CrmModalDocument,
} from 'prismicio-types';
import type { EnrichedHeaderDocument } from '@/types';
import { getTreezStores } from '@/utils';

export const GLOBAL_KEY = 'global';

/** Mirrors the `_STORE_TERMS` cookie. */
export type StoreTermsMap = Record<string, boolean>;

export const addStoreTerm = (
  current: StoreTermsMap | null | undefined,
  key: string,
): StoreTermsMap => ({ ...(current ?? {}), [key]: true });

/** `buildAgeAcceptance`'s default window, used when the global acceptance carries no expiry. */
const THIRTY_DAYS = 30 * 24 * 60 * 60;

/**
 * `_STORE_TERMS` is one cookie, so every write re-dates the whole map — it has to ride the global
 * window. Pass `terms_accepted_exp_date` from `_TERMS_ACCEPTED`.
 */
export const resolveStoreTermsMaxAge = (expDate?: string | null): number => {
  if (!expDate) return THIRTY_DAYS;

  const remaining = Math.floor(
    (new Date(expDate).getTime() - Date.now()) / 1000,
  );
  return remaining > 0 ? remaining : THIRTY_DAYS;
};

// `/age-gate` URL for an in-session store switch
export const buildAgeGateUrl = (storeId: string, returnUrl: string): string =>
  `/age-gate?store=${encodeURIComponent(
    storeId,
  )}&returnUrl=${encodeURIComponent(returnUrl)}`;

export interface IAgeAcceptanceState {
  terms_accepted: boolean;
  terms_accepted_exp_date: Date | null;
}

/**
 * Global acceptance state + `_TERMS_ACCEPTED` payload for an accepted gate. Per-store acceptance
 * is separate ({@link addStoreTerm}). Remember Me is global, so `rememberMeDays` is the doc value.
 */
export const buildAgeAcceptance = ({
  rememberMeDays,
  rememberChecked,
}: {
  rememberMeDays?: number | null;
  rememberChecked: boolean;
}): {
  state: IAgeAcceptanceState;
  cookie: IAgeTermsData;
  cookieMaxAge: number;
} => {
  const now = new Date();
  const days = rememberChecked && rememberMeDays ? rememberMeDays : undefined;
  const expDate = days ? addDays(now, days) : null;

  const cookieMaxAgeDays = expDate
    ? Math.ceil((expDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
    : 30;

  return {
    state: {
      terms_accepted: true,
      terms_accepted_exp_date: expDate,
    },
    cookie: {
      terms_accepted: true,
      terms_accepted_exp_date: expDate?.toISOString() || null,
    },
    cookieMaxAge:
      cookieMaxAgeDays > 0
        ? cookieMaxAgeDays * 24 * 60 * 60
        : 30 * 24 * 60 * 60,
  };
};

const normalizeSlug = (slug?: string | null): string =>
  (slug ?? '').trim().toLowerCase();

const parseSlugCsv = (csv?: string | null): string[] =>
  (csv ?? '').split(',').map(normalizeSlug).filter(Boolean);

const getStoreUrlSlug = (shortName: string): string =>
  normalizeSlug(
    getTreezStores()?.find(
      store => normalizeSlug(store?.short_name) === shortName,
    )?.url_slug,
  );

export interface IResolvedAgeGate {
  /**
   * A matching slice's `primary` or the static doc data. Typed as `AgeGateDocumentData` because
   * the slice `primary` shares every field that gets read; it only lacks `slices`.
   */
  content: AgeGateDocumentData;
  /** The matched store slug, or `'global'`. */
  key: string;
  matched: boolean;
}

/**
 * Effective Age Gate content for a location: the FIRST active `age_gate` slice whose
 * `location_slug` CSV contains the store's `url_slug`, else the static/global data.
 *
 * Takes the store's `short_name` — that is the identity the rest of the feature runs on, and it
 * stays the acceptance `key`. Only the CMS-authored `location_slug` is compared against `url_slug`.
 */
export const resolveAgeGateContent = (
  ageGateData?: AgeGateDocumentData | null,
  storeShortName?: string | null,
): IResolvedAgeGate => {
  const fallback: IResolvedAgeGate = {
    content: (ageGateData ?? {}) as AgeGateDocumentData,
    key: GLOBAL_KEY,
    matched: false,
  };

  const slug = normalizeSlug(storeShortName);
  if (!ageGateData || !slug) {
    return fallback;
  }

  const urlSlug = getStoreUrlSlug(slug);
  if (!urlSlug) {
    return fallback;
  }

  // The custom type may gain other slice types later.
  const slices = ((ageGateData.slices ?? []) as AgeGateSlice[]).filter(
    slice => slice?.slice_type === 'age_gate',
  );
  const match = slices.find(slice => {
    const primary = slice.primary;
    return (
      Boolean(primary?.active) &&
      parseSlugCsv(primary?.location_slug).includes(urlSlug)
    );
  });

  if (!match) {
    return fallback;
  }

  return {
    content: match.primary as unknown as AgeGateDocumentData,
    key: slug,
    matched: true,
  };
};

/**
 * `true` when a store needs no per-location gate, or already cleared it per `_STORE_TERMS`. Shared
 * by the store-change redirect and `StoreValidator` so both coordinate off one source.
 */
export const isStoreAgeGateSatisfied = (
  ageGateData: AgeGateDocumentData | null | undefined,
  storeShortName: string | null | undefined,
  storeTerms: StoreTermsMap | null | undefined,
): boolean => {
  const { content, key, matched } = resolveAgeGateContent(
    ageGateData,
    storeShortName,
  );
  if (!matched || !content.type) return true;

  return storeTerms?.[key] === true;
};

export const buildResponsiveBackgroundUrls = (
  background?: ImageField<'mobile'>,
) => {
  const desktopBaseUrl = background?.url ?? '';
  const mobileBaseUrl = background?.mobile?.url ?? '';

  const optimizedUrl = (url: string, width: number) =>
    url ? `${url}&auto=compress,format&w=${width}&q=70` : '';

  const placeholderUrl = (url: string) =>
    url ? `${url}&auto=compress,format&w=24&q=20&blur=50` : '';

  return {
    desktopDefault: optimizedUrl(desktopBaseUrl, 1536),
    desktopRetina: optimizedUrl(desktopBaseUrl, 2008),
    mobileDefault: optimizedUrl(mobileBaseUrl || desktopBaseUrl, 520),
    mobileRetina: optimizedUrl(mobileBaseUrl || desktopBaseUrl, 640),
    desktopPlaceholder: placeholderUrl(desktopBaseUrl),
    mobilePlaceholder: placeholderUrl(mobileBaseUrl),
  };
};

/**
 * Server-ready Age Gate data for a location: effective content + acceptance `key`, with empty
 * agreements dropped. `null` when the doc is empty — the caller should auto-accept.
 */
export const getProcessedAgeGate = (
  rawData: IStoreMetadata,
  storeShortName?: string | null,
): { data: IProcessedAgeGateData; key: string; matched: boolean } | null => {
  if (!rawData?.ageGate?.data) return null;

  const { content, key, matched } = resolveAgeGateContent(
    rawData.ageGate.data,
    storeShortName,
  );

  return {
    key,
    matched,
    data: {
      ageGate: {
        ...rawData.ageGate,
        data: {
          ...content,
          remember_me: rawData.ageGate.data.remember_me,
          agreements: content.agreements?.filter(({ text }) => !!text) ?? [],
        },
      },
      crmModal: rawData.crmModal,
      header: rawData.header,
    } as unknown as IProcessedAgeGateData,
  };
};

export interface IAgeTermsData {
  terms_accepted: boolean;
  terms_accepted_exp_date: string | null;
}
export interface IProcessedAgeGateData {
  ageGate: {
    data: AgeGateDocumentData;
  };
  crmModal: CrmModalDocument;
  header: EnrichedHeaderDocument;
}
